import React from 'react';
import { Chip, IconButton, Tooltip, Box } from '@material-ui/core';
import moment from 'moment';
import { falsy, fNumber, getUnitDetails } from 'utils/functions';
import { useTranslation } from 'react-i18next';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import LaunchIcon from '@material-ui/icons/Launch';
import ErrorIcon from '@material-ui/icons/Error';
import { CalendarTodayOutlined, CategoryOutlined } from '@material-ui/icons';
import { Row } from 'commons/components/DataTable/Row';
import { TruncateTextToolTip } from 'commons/components/DataTable/TruncateTextToolTip';
import { shouldUseCallOffInAutomationModals } from '../../../../utils/tenantFeatures';
import { formatDurationMinutes } from '../../../../utils/formatDurationMinutes';

function ItemAutomation({
  classes,
  process,
  objects,
  openMenuHandler,
  processOnClick,
  headerList,
  isMobileDevice,
}) {
  const { t } = useTranslation();
  const isClosed = !!process.closedAt;
  const { unitCode } = getUnitDetails(objects.accounts);
  const shouldShowCallOffColumn = shouldUseCallOffInAutomationModals();
  const processTypeName = (process?.processTypeName || '').toLowerCase().trim();
  const isAutomaticReception = processTypeName === 'recepción' || processTypeName === 'recepcion';
  const lots = Object.values(objects?.lots || {});
  const entryLots = lots.filter((lot) => lot?.destinyProcessId === process.id);
  const departureLots = lots.filter((lot) => lot?.originProcessId === process.id);

  const fallbackEntryWeight = entryLots.reduce(
    (acum, lot) => acum + (Number(lot?.quantity) || 0),
    0,
  );
  const fallbackDepartureWeight = departureLots.reduce(
    (acum, lot) => acum + (Number(lot?.quantity) || 0),
    0,
  );
  const entryWeight = Number.isFinite(Number(process?.displayEntryWeight))
    ? Number(process.displayEntryWeight)
    : fallbackEntryWeight;
  const departureWeight = Number.isFinite(Number(process?.displayDepartureWeight))
    ? Number(process.displayDepartureWeight)
    : fallbackDepartureWeight;
  const wasteWeight = entryWeight - departureWeight;
  const displayWasteWeight = Number.isFinite(Number(process?.displayWasteWeight))
    ? Number(process.displayWasteWeight)
    : wasteWeight;

  const getNumber = () => (
    <div className="d-flex flex-direction-column align-items-start">
      <TruncateTextToolTip
        text={`#${process.id || '-'}`}
        style={{
          color: isClosed ? '#e57373' : '#47AA4D',
          width: 100,
          paddingLeft: '10px',
        }}
      />
      <Chip
        label={isClosed ? t('Closed') : t('Open')}
        size="small"
        style={{
          backgroundColor: isClosed ? '#ffebee' : '#e8f5e8',
          color: isClosed ? '#c62828' : '#2e7d32',
          fontSize: '0.8rem',
          height: '1.2rem',
          marginTop: '0.4rem',
          fontWeight: 'bold',
          padding: '0.2rem',
        }}
      />
    </div>
  );

  const getLots = () => (
    <TruncateTextToolTip
      text={shouldShowCallOffColumn ? process.callOff || '-' : process.lotNumber || '-'}
    />
  );

  const getSupplier = () => <TruncateTextToolTip text={process.supplierName || '-'} />;

  const getCategory = () => <TruncateTextToolTip text={process.transitionType || '-'} />;

  const getProcessType = () => <TruncateTextToolTip text={process.processTypeName || '-'} />;

  const getProduct = () => <TruncateTextToolTip text={process.productName || '-'} />;

  const getVariety = () => <TruncateTextToolTip text={process.varietyName || '-'} />;

  const getPerformanceColor = (item) => {
    const lowThreshold = Number(
      item?.lowTimeThreshold ??
      item?.ProcessType?.lowTimeThreshold ??
      item?.processType?.lowTimeThreshold,
    );
    const highThreshold = Number(
      item?.highTimeThreshold ??
      item?.ProcessType?.highTimeThreshold ??
      item?.processType?.highTimeThreshold,
    );
    const hasThresholds =
      Number.isFinite(lowThreshold) || Number.isFinite(highThreshold);

    if (!hasThresholds) return null;

    const duration = Number(item?.durationMinutes);

    if (Number.isFinite(duration)) {
      if (Number.isFinite(lowThreshold) && duration < lowThreshold) return '#4caf50';
      if (Number.isFinite(highThreshold) && duration > highThreshold) return '#f44336';
      if (Number.isFinite(lowThreshold) || Number.isFinite(highThreshold)) return '#ff9800';
    }

    const status = item?.performanceStatus;
    switch (status) {
      case 'BELOW':
      case 'FAST':
        return '#4caf50'; // Verde
      case 'OPTIMAL':
      case 'MEDIUM':
        return '#ff9800'; // Naranja
      case 'ABOVE':
      case 'SLOW':
        return '#f44336'; // Rojo
      default: return '#757575'; // Gris
    }
  };

  const getPerformanceChipStyle = (item) => {
    const performanceColor = getPerformanceColor(item);

    if (!performanceColor) {
      return {
        backgroundColor: '#f5f5f5',
        color: '#546e7a',
        border: '1px solid #cfd8dc',
        fontSize: '0.7rem',
        height: '1.2rem',
        marginTop: '0.2rem',
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
      };
    }

    return {
      backgroundColor: performanceColor,
      color: '#fff',
      fontSize: '0.7rem',
      height: '1.2rem',
      marginTop: '0.2rem',
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
    };
  };

  const getProcessDate = () => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <span>{moment(process.date).format('DD-MM-YYYY')}</span>
      {!isAutomaticReception &&
        process.durationMinutes !== null &&
        process.durationMinutes !== undefined && (
        <Chip
          label={Number(process.durationMinutes) >= 60
            ? formatDurationMinutes(process.durationMinutes)
            : `${Math.round(process.durationMinutes)} ${t('process.table.minutes')}`}
          size="small"
          style={getPerformanceChipStyle(process)}
        />
      )}
    </div>
  );

  const getCreatedBy = () => <TruncateTextToolTip text={process.harvester || '-'} lines={1} />;

  const getEntry = () => {
    const label = `${fNumber.format(entryWeight)} ${unitCode}`;
    return (
      <Tooltip title={label} placement="top">
        <Chip
          icon={<ExitToAppIcon />}
          size="small"
          label={label}
          className={classes.blueChip}
          color="primary"
        />
      </Tooltip>
    );
  };

  const getExit = () => {
    const label = `${fNumber.format(departureWeight)} ${unitCode}`;
    return (
      <Tooltip title={label} placement="top">
        <Chip
          icon={<LaunchIcon />}
          size="small"
          label={label}
          className={classes.greenChip}
          color="primary"
        />
      </Tooltip>
    );
  };

  const getDecrease = () => {
    const label = `${fNumber.format(displayWasteWeight)} ${unitCode}`;
    return (
      <Tooltip title={label} placement="top">
        <Chip
          icon={<ErrorIcon />}
          size="small"
          label={label}
          className={classes.redChip}
          color="primary"
        />
      </Tooltip>
    );
  };

  const getOptions = () =>
    falsy(process.closedAt) ? (
      <Box>
        <IconButton
          data-id={process.id}
          onClick={(e) => {
            e.stopPropagation();
            openMenuHandler(e);
          }}>
          <MoreVertIcon />
        </IconButton>
      </Box>
    ) : null;

  const getArrayItem = () => [
    { value: getNumber() },
    { value: getLots(), icon: null },
    { value: getSupplier(), icon: null },
    { value: getCategory(), icon: null },
    { value: getProcessType(), icon: <CategoryOutlined /> },
    { value: getProduct(), icon: null },
    { value: getVariety(), icon: null },
    {
      value: getProcessDate(),
      icon: <CalendarTodayOutlined style={{ fontSize: '1.1rem' }} />,
    },
    { value: getCreatedBy(), icon: null },
    { value: getEntry(), icon: null },
    { value: getExit(), icon: null },
    { value: getDecrease(), icon: null },
    { value: <Box className="row-options">{getOptions()}</Box>, icon: null },
  ];

  return (
    <div style={{ cursor: 'pointer' }}>
      <Row
        row={getArrayItem()}
        handleOnClick={processOnClick(process.id)}
        headerList={headerList}
        isMobileDevice={isMobileDevice}
      />
    </div>
  );
}

export default ItemAutomation;
